import { useQuery } from '@apollo/client';
import { TextField } from '@components/TextField';
import { IOption } from '@components/Filter/filter.utils';
import Colors from '@constants/Colors';
import { useDebounce } from '@hooks/useDebounce';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '../AutoComplete';

interface InputAutocompleteProps {
    id?: string;
    name: string;
    placeholder?: string;
    label?: string; // Label for the TextField
    multiple?: boolean;
    options?: IOption[]; // Static options
    query: any; // GraphQL query
    queryVariables?: any; // Variables for the query
    getOptions: (data: any) => IOption[]; // Function to extract options from the query response
    setChipLabels?: any; // Labels for chips
    size?: 'small' | 'medium';
    disabled?: boolean;
}

const InputAutocompleteAsync: React.FC<InputAutocompleteProps> = ({
    id,
    name,
    placeholder,
    multiple = false,
    query,
    queryVariables,
    getOptions,
    setChipLabels,
    size = 'small',
    disabled = false,
}) => {
    const { control, setValue, watch } = useFormContext();
    const [options, setOptions] = useState<IOption[]>([]);
    const [queryString, setQueryString] = useState<string>('');
    const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false); // State to manage dropdown visibility
    const hasFetchedInitial = useRef<boolean>(false);

    // Use ref to store the latest getOptions to avoid recreating fetchOptions on every render
    const getOptionsRef = useRef(getOptions);
    useEffect(() => {
        getOptionsRef.current = getOptions;
    }, [getOptions]);

    const { data, loading, refetch } = useQuery(query, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: '',
            filter: {},
        },
    });

    const initialOptions = useMemo(() => {
        return isEmpty(data) ? [] : getOptionsRef.current(data);
    }, [data]);

    const debouncedQueryString = useDebounce(queryString, 300);

    const handleInputChange = useCallback((event: React.ChangeEvent<{}>, value: string, reason: string) => {
        // Always update query string for search, regardless of reason
        setQueryString(value ?? '');
    }, []);

    const fetchOptions = useCallback(
        async (searchQuery: string) => {
            try {
                const { data } = await refetch({
                    query: searchQuery || '',
                    filter: {},
                });
                const fetchedOptions = getOptionsRef.current(data);
                if (setChipLabels && id) {
                    setChipLabels(prev => {
                        return {
                            ...prev,
                            [id]: fetchedOptions,
                        };
                    });
                }
                setOptions(fetchedOptions);
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        },
        [refetch, setChipLabels, id],
    );

    useEffect(() => {
        // Skip the initial fetch if we already have data from useQuery and no search query
        if (!hasFetchedInitial.current && !debouncedQueryString && !isEmpty(data)) {
            hasFetchedInitial.current = true;
            setOptions(initialOptions);
            return;
        }

        hasFetchedInitial.current = true;
        fetchOptions(debouncedQueryString);
    }, [debouncedQueryString, fetchOptions, data, initialOptions]);

    const handleOpen = useCallback(() => {
        setIsOptionsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setIsOptionsOpen(false);
    }, []);

    const handleEndAdornmentClick = useCallback(() => {
        setIsOptionsOpen(prev => !prev);
    }, []);

    // compute selected options from stored raw value(s) in the form
    const storedValue = watch(name);
    const allOptions = (isEmpty(options) ? initialOptions : options) as IOption[];
    const findSelectedOptions = (): IOption | IOption[] | null => {
        if (multiple) {
            const values: string[] = Array.isArray(storedValue) ? storedValue : [];
            return allOptions.filter(opt => values.includes(opt.value));
        }
        const value: string = typeof storedValue === 'string' ? storedValue : '';
        return allOptions.find(opt => opt.value === value) || null;
    };

    return (
        <Controller
            name={name}
            control={control}
            disabled={disabled}
            render={({ field, fieldState: { error } }) => (
                <Autocomplete
                    id={id}
                    multiple={multiple}
                    disabled={field.disabled}
                    options={allOptions}
                    defaultValue={findSelectedOptions() as any}
                    getOptionLabel={(option: IOption) => option.label || option.value}
                    onChange={(_, value, reason) => {
                        if (multiple) {
                            const newValues = (value as IOption[] | null)?.map(v => v.value) || [];
                            setValue(name, newValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            field.onChange(newValues);
                        } else {
                            const newValue = (value as IOption | null)?.value || '';
                            setValue(name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                            field.onChange(newValue);
                        }
                        if (reason === 'clear') {
                            handleClose();
                        }
                    }}
                    onInputChange={handleInputChange}
                    isOptionEqualToValue={(option: IOption, value: IOption) => option.value === value.value}
                    filterSelectedOptions
                    loading={loading}
                    loadingText={<div style={{ display: 'flex', alignItems: 'center' }}>Chargement...</div>}
                    noOptionsText="Aucun résultat"
                    open={multiple ? isOptionsOpen : undefined}
                    onOpen={handleOpen}
                    onClose={(_, reason) => {
                        if (multiple && reason === 'selectOption') return;
                        handleClose();
                    }}
                    popupIcon={
                        isOptionsOpen ? (
                            <i
                                className="fi fi-br-check"
                                onClick={handleEndAdornmentClick}
                                style={{ color: Colors.PRIMARY, fontSize: 16, cursor: 'pointer', marginRight: 4 }}
                            />
                        ) : undefined
                    }
                    slotProps={{
                        popupIndicator: {
                            style: {
                                transform: 'none',
                            },
                        },
                    }}
                    ChipProps={{
                        deleteIcon: <i className="fi fi-br-cross-small" style={{ fontSize: 12 }} />,
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            size={size}
                            placeholder={placeholder || 'Tapez pour rechercher...'}
                            error={!!error}
                            helperText={error ? error.message : ''}
                            variant="outlined"
                            sx={{
                                background: theme => theme.palette.dashboard.main,
                            }}
                        />
                    )}
                />
            )}
        />
    );
};

export default InputAutocompleteAsync;
